import {
  Template,
  Templates,
  SceneData,
  Scene,
  EditedSceneData,
  MergedSceneData,
} from './types/deluge';

import nestedUtil from '../utils/nestedUtils';

//

/**
 * Takes a Scene and a set of Templates and composes the SceneData for the Scene.

 * @param {Scene} scene
 * @param {Templates} templates
 * @param {boolean} sortKeys - Whether to sort the keys in the SceneData. Defaults to true.
 * @returns {SceneData} The composed SceneData
 */
export function composeSceneDataFromScene(
  scene: Scene,
  templates: Templates,
  sortKeys = true
): MergedSceneData {
  if (!templates) {
    throw new Error(`Templates must be provided!`);
  }

  scene = JSON.parse(JSON.stringify(scene)); // Deep copy to prevent mutation of original data
  templates = JSON.parse(JSON.stringify(templates)); // Deep copy to prevent mutation of original data

  let sceneTemplate = templates[scene.scene_name];

  if (!sceneTemplate) {
    throw new Error(`Template not found for scene ${scene.scene_name}`);
  }

  // Propagate nested_template flag from source template for presets
  sceneTemplate = addNestedInfoToPreset(sceneTemplate, templates);

  let sourceData = sceneTemplate.src_scene_data;
  if (
    sceneTemplate.extra_info?.preset &&
    sceneTemplate.extra_info.src_template
  ) {
    sourceData =
      templates[sceneTemplate.extra_info.src_template].src_scene_data;
  }

  const templateData = sceneTemplate.scene_data;
  const sceneData = scene.svars;

  let composition = {};

  if (sceneTemplate.extra_info.nested_template) {
    composition = composeSceneDataFromNestedTemplate(
      sceneTemplate,
      templates,
      sourceData,
      templateData,
      sceneData
    );
  } else {
    composition = composeSceneData(
      'scene',
      sourceData,
      templateData,
      sceneData
    );
  }

  if (sortKeys) {
    nestedUtil.sortNested(composition, sceneTemplate, templates);
  }
  return composition;
}

function addNestedInfoToPreset(
  template: Template,
  templates: Templates
): Template {
  if (!template.extra_info?.preset) return template;
  if (!template.extra_info.src_template) return template;

  const original = templates[template.extra_info.src_template];

  if (!original) return template;

  if (original.extra_info.nested_template) {
    template.extra_info.nested_template = true;
  }

  return template;
}

/**
 * Takes a Template and a set of Templates and composes the SceneData for the Template.

 * @param {Template} template
 * @param {Templates} templates
 *  * @param {boolean} sortKeys - Whether to sort the keys in the SceneData. Defaults to true.
 * @returns {SceneData} The composed SceneData
 */
export function composeSceneDataFromTemplate(
  template: Template,
  templates: Templates,
  sortKeys = true
): EditedSceneData {
  template = JSON.parse(JSON.stringify(template)); // Deep copy to prevent mutation of original data
  templates = JSON.parse(JSON.stringify(templates)); // Deep copy to prevent mutation of original data
  let sourceData = template.src_scene_data;
  if (template.extra_info?.preset && template.extra_info.src_template) {
    sourceData = templates[template.extra_info.src_template].src_scene_data;
  }
  const templateData = template.scene_data;

  let composition = {};

  template = addNestedInfoToPreset(template, templates);

  if (template.extra_info.nested_template) {
    composition = composeSceneDataFromNestedTemplate(
      template,
      templates,
      sourceData,
      templateData,
      {}
    );
  } else {
    composition = composeSceneData('template', sourceData, templateData);
  }

  if (sortKeys) {
    nestedUtil.sortNested(composition, template, templates);
  }
  return composition;
}

function composeSceneDataFromNestedTemplate(
  topLevelTemplate: Template,
  templates: Templates,
  sourceData: SceneData,
  templateData: SceneData,
  sceneData: SceneData
): MergedSceneData {
  const [skeletonBase, skeleton] = getSkeletonStructure(
    sourceData,
    templateData,
    sceneData
  );

  addToSkeleton(skeletonBase, skeleton, topLevelTemplate, templates);
  return skeleton;
}

function addToSkeleton(
  skeletonBase: SkeletonBase,
  skeletonNode: SceneData,
  template: Template,
  templates: Templates
) {
  let sourceData = template.src_scene_data;
  if (template.extra_info?.preset && template.extra_info.src_template) {
    sourceData = templates[template.extra_info.src_template].src_scene_data;
  }
  const templateData = template.scene_data;

  fleshOutSkeletonNodeValues(skeletonBase, skeletonNode, sourceData);
  addTemplateToNode(skeletonNode, sourceData, templateData);
  addRemainingSourceKeysAndValuesToNode(skeletonNode, sourceData);

  for (const subData of Object.values(skeletonNode)) {
    if ('scene_data' in subData === false) continue;
    if (typeof subData.value !== 'string') continue;

    // subData is a Port
    const nextNode = subData.scene_data;
    const nextTemplate = templates[subData.value];

    if (nextNode && nextTemplate)
      addToSkeleton(skeletonBase, nextNode, nextTemplate, templates);
  }
}

/**
Flesh out the existing values in this node using SRC
**/
function fleshOutSkeletonNodeValues(
  skeletonBase: SkeletonBase,
  skeletonNode: SceneData,
  sourceData: SceneData
) {
  // This should just be the ports at this point
  for (const [key, subData] of Object.entries(skeletonNode)) {
    // If the skeletonBase is the source, then we don't need to flesh out the values
    if (skeletonBase === 'source') {
      (subData as any)['keyInherited'] = true;
      continue;
    }

    if ('value' in subData) {
      (subData as any)['keyInherited'] = false;
      // console.log(subData);
    } else {
      (subData as any)['keyInherited'] = true;
    }

    // If the key is not in the source, we don't need to flesh out the values, this means the key is most likely deprecated
    if (!sourceData[key]) continue;

    for (const [srcKey, srcValue] of Object.entries(sourceData[key])) {
      // Get all other keys that we don't already have.
      if (srcKey in subData === false) {
        (subData as any)[srcKey] = srcValue;
      }
    }
  }
}

/**
Pour in the template data and flesh out the existing values
**/
function addTemplateToNode(
  skeletonNode: SceneData,
  sourceData: SceneData,
  templateData: SceneData
) {
  //  Pour in the template data
  for (const [key, subData] of Object.entries(templateData)) {
    if (key in sourceData === false) continue;

    if (key in skeletonNode === false) {
      if (key.startsWith('$')) continue; // Skip adding ports from Our Template
      // Deep-clone the template subData to avoid sharing object references
      // between multiple skeleton nodes that may reuse the same key names.
      try {
        skeletonNode[key] = JSON.parse(JSON.stringify(subData));
      } catch (e) {
        skeletonNode[key] = subData;
      }

      // copy the remaining keys and values from SRC (clone values defensively)
      for (const [srcKey, srcValue] of Object.entries(sourceData[key])) {
        if (srcKey in skeletonNode[key] === false) {
          try {
            (skeletonNode[key] as any)[srcKey] = JSON.parse(
              JSON.stringify(srcValue)
            );
          } catch (e) {
            (skeletonNode[key] as any)[srcKey] = srcValue;
          }
          (skeletonNode[key] as any)['keyInherited'] = true;
        }
      }
    }
  }
}

function addRemainingSourceKeysAndValuesToNode(
  skeletonNode: SceneData,
  sourceData: SceneData
) {
  for (const [key, subData] of Object.entries(sourceData)) {
    if (key in skeletonNode === false) {
      // Deep-clone source nodes to avoid shared references in the composed skeleton
      try {
        skeletonNode[key] = JSON.parse(JSON.stringify(subData));
      } catch (e) {
        skeletonNode[key] = subData;
      }
      // All sourceData is inherited
      (skeletonNode[key] as any)['keyInherited'] = true;
    }
  }
}

type SkeletonBase = 'scene' | 'template' | 'source';

/**
 * Nested Templates do not inherit structure, as such we need to find the highest level scene_data to act as our skeleton.
 *
 * From highest priority to lowest:
 * Scene > Template > SRC
 *
 *  returns a deep copy of the structure.
 */
function getSkeletonStructure(
  sourceData: SceneData,
  templateData: SceneData,
  sceneData: SceneData
): [SkeletonBase, MergedSceneData] {
  if (Object.keys(sceneData).length > 0)
    return ['scene', JSON.parse(JSON.stringify(sceneData))];
  if (Object.keys(templateData).length > 0)
    return ['template', JSON.parse(JSON.stringify(templateData))];

  return ['source', JSON.parse(JSON.stringify(sourceData))];
}

type Mode = 'scene' | 'template';

function composeSceneData(
  mode: Mode,
  sourceData: SceneData,
  templateData: SceneData,
  sceneData: SceneData | undefined = undefined
): MergedSceneData {
  const output: MergedSceneData = JSON.parse(JSON.stringify(sourceData));

  for (const value of Object.values(output)) {
    // All sourceData is inherited
    (value as any)['keyInherited'] = true;
  }

  const templateInheritance = mode !== 'template';

  if (templateData) addToOutput(templateData, output, templateInheritance);
  if (sceneData) addToOutput(sceneData, output);

  return output;
}

function addToOutput(
  dataToAdd: SceneData,
  output: MergedSceneData,
  inherited: boolean = false
) {
  dataToAdd = JSON.parse(JSON.stringify(dataToAdd));

  for (const [key, value] of Object.entries(dataToAdd)) {
    // If the key is not in the output, we don't want to add it
    if (key in output === false) {
      if (value.persistent_key) {
        output[key] = value; // for cricket app
      } else {
        continue;
      }
    }

    // DEFENSIVE: Check if output[key] is actually an object before setting properties
    if (
      !output[key] ||
      typeof output[key] !== 'object' ||
      Array.isArray(output[key])
    ) {
      console.warn(
        `Invalid scene data structure for key: ${key} in output. Expected object, got ${typeof output[key]}. Normalizing...`
      );
      // Normalize the data structure
      output[key] = {
        value: output[key],
        type: typeof output[key],
        keyInherited: inherited,
      };
    }

    // DEFENSIVE: Validate value is an object with expected properties
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      (output[key] as any)['value'] = (value as any)['value'];
      (output[key] as any)['type'] = (value as any)['type'];
      (output[key] as any)['keyInherited'] = inherited;
    } else {
      console.warn(
        `Invalid scene data structure for key: ${key} in dataToAdd. Expected object with value/type properties, got ${typeof value}. Skipping merge.`
      );
      // If value is malformed, at least preserve the existing output structure
      (output[key] as any)['keyInherited'] = inherited;
    }
  }
}
